import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Target, Globe, TrendingUp, Lightbulb, Brain, 
  DollarSign, AlertTriangle, Zap, Star, Shield, Eye, 
  Heart, Flame, Award, TestTube, Clock, MapPin, BarChart3, 
  ThumbsUp, MessageCircle, Share2, Bookmark, CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import VideoAnalysisResults from './VideoAnalysisResults';

const ComprehensiveAnalysisResults = ({ result, darkMode }) => {
  // Debug logging
  console.log('📊 Comprehensive Analysis - content_type:', result.content_type);
  console.log('📊 Has video_insights:', !!result.video_insights);
  console.log('📊 Full result:', result);
  
  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Video-Specific Sections (if video content) */}
      {result.content_type === 'video' && result.video_insights && (
        <>
          {console.log('✅ Rendering VideoAnalysisResults')}
          <VideoAnalysisResults result={result} darkMode={darkMode} />
        </>
      )}
      
      {/* Core Emotional Analysis - Mobile Optimized */}
      <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
        <CardHeader className="pb-2 sm:pb-6 px-3 sm:px-6 pt-3 sm:pt-6">
          <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            Core Emotional Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4 px-3 sm:px-6 pb-3 sm:pb-6">
          {/* Dominant Emotion - Mobile Optimized */}
          <div className="text-center p-3 sm:p-6 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="text-xs sm:text-sm text-white/80 mb-1">Dominant Emotion</div>
            <div className="text-xl sm:text-4xl font-bold text-white capitalize">{result.dominant_emotion}</div>
          </div>

          {/* PAD Scores - Mobile Optimized */}
          <div className="grid grid-cols-3 gap-1.5 sm:gap-4">
            <div className={`p-2 sm:p-4 rounded-lg text-center ${darkMode ? 'bg-blue-900/40 border-2 border-blue-500' : 'bg-blue-50'}`}>
              <div className={`text-[10px] sm:text-sm mb-0.5 sm:mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pleasure</div>
              <div className={`text-base sm:text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{Math.round(result.valence * 100)}%</div>
            </div>
            <div className={`p-2 sm:p-4 rounded-lg text-center ${darkMode ? 'bg-purple-900/40 border-2 border-purple-500' : 'bg-purple-50'}`}>
              <div className={`text-[10px] sm:text-sm mb-0.5 sm:mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Arousal</div>
              <div className={`text-base sm:text-3xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{Math.round(result.arousal_level * 100)}%</div>
            </div>
            <div className={`p-2 sm:p-4 rounded-lg text-center ${darkMode ? 'bg-indigo-900/40 border-2 border-indigo-500' : 'bg-indigo-50'}`}>
              <div className={`text-[10px] sm:text-sm mb-0.5 sm:mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Dominance</div>
              <div className={`text-base sm:text-3xl font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{Math.round(result.dominance * 100)}%</div>
            </div>
          </div>

          {/* Full Emotion Profile - Mobile Optimized */}
          <div>
            <h4 className={`font-semibold mb-2 sm:mb-3 text-sm sm:text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>Complete Emotion Profile</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              {Object.entries(result.emotions || {}).sort(([,a], [,b]) => b - a).map(([emotion, score]) => (
                <div key={emotion} className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700/50 border border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                  <div className={`text-[10px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} capitalize mb-1`}>{emotion}</div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 sm:h-2">
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 h-1.5 sm:h-2 rounded-full" style={{width: `${score}%`}}></div>
                    </div>
                    <span className={`text-xs sm:text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Math.round(score)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demographic Insights - Mobile Optimized */}
      <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
        <CardHeader className="px-3 sm:px-6 py-3 sm:py-6">
          <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            <span className="text-xs sm:text-base">Audience Demographics (20+ Segments)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          <div className="space-y-2 sm:space-y-3">
            {/* Basic Demographics - Mobile Optimized */}
            <div className="mb-3 sm:mb-4">
              <h5 className={`text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Core Audiences</h5>
              <div className="grid gap-1.5 sm:gap-2">
                {Object.entries(result.audience_reactions || {}).map(([group, reaction]) => {
                  const reactionText = typeof reaction === 'object' && reaction.reaction ? String(reaction.reaction) : String(reaction);
                  return (
                    <div key={group} className={`p-2 sm:p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                      <div className="flex justify-between items-start mb-0.5 sm:mb-1">
                        <span className="text-xs sm:text-sm font-semibold text-blue-600">{group}</span>
                      </div>
                      <p className={`text-[11px] sm:text-sm leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{reactionText}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Expanded Demographics - Mobile Optimized */}
            {result.expanded_demographics && (
              <div>
                <h5 className={`text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Detailed Segments</h5>
                <div className="grid gap-1.5 sm:gap-2">
                  {Object.entries(result.expanded_demographics).map(([segment, data]) => {
                    const reactionText = typeof data === 'object' && data.reaction ? String(data.reaction) : String(data);
                    const engagementValue = typeof data === 'object' && data.engagement ? Number(data.engagement) : null;
                    return (
                      <div key={segment} className={`p-2 sm:p-3 rounded-lg border-l-4 border-purple-500 ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                        <div className="flex justify-between items-start mb-0.5 sm:mb-1">
                          <span className="text-xs sm:text-sm font-semibold text-purple-600">{segment}</span>
                          {engagementValue !== null && (
                            <Badge className="bg-purple-600 text-white text-[10px] sm:text-xs px-1.5 py-0.5">{engagementValue}%</Badge>
                          )}
                        </div>
                        <p className={`text-[11px] sm:text-sm leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{reactionText}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Global Emotional Response - Mobile Optimized */}
      <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
        <CardHeader className="px-3 sm:px-6 py-3 sm:py-6">
          <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            <span className="text-xs sm:text-base">Global Emotional Response</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          {result.global_emotional_response && Object.keys(result.global_emotional_response).length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-6">
                {Object.entries(result.global_emotional_response).map(([region, data]) => (
                  <div key={region} className={`p-2 sm:p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <span className={`font-semibold text-xs sm:text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {region}
                      </span>
                      <Badge className="bg-gradient-to-r from-green-600 to-emerald-600 text-white text-[10px] sm:text-xs px-1.5 py-0.5">
                        {data.percentage}%
                      </Badge>
                    </div>
                    <p className={`text-sm sm:text-lg font-medium leading-snug ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                      {data.response}
                    </p>
                    <div className="mt-2 sm:mt-3">
                      <div className={`h-1.5 sm:h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                          style={{ width: `${data.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Global Average - Mobile Optimized */}
              {(() => {
                const percentages = Object.values(result.global_emotional_response).map(d => d.percentage);
                const average = Math.round(percentages.reduce((a, b) => a + b, 0) / percentages.length);
                let rating = 'Good';
                if (average >= 80) rating = 'Excellent';
                else if (average >= 70) rating = 'Very Good';
                else if (average >= 60) rating = 'Good';
                else if (average >= 50) rating = 'Fair';
                else rating = 'Needs Improvement';
                
                return (
                  <div className={`p-3 sm:p-4 rounded-lg text-center ${darkMode ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-700' : 'bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300'}`}>
                    <div className={`text-xs sm:text-sm font-semibold mb-1 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Global Average</div>
                    <div className="flex items-center justify-center gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-green-600">{average}%</div>
                      <Badge className="bg-green-600 text-white text-xs sm:text-base px-2 sm:px-3 py-0.5 sm:py-1">{rating}</Badge>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className={`p-4 sm:p-6 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <Globe className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-400" />
              <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Global regional analysis available in detailed results
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Industry Fit Analysis */}
      {result.industry_fit && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Target className="w-5 h-5 text-orange-600" />
              Industry Fit Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(result.industry_fit).map(([industry, data]) => (
                <div key={industry} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-orange-50 to-yellow-50'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="font-semibold text-orange-600">{industry}</h5>
                    <Badge className="bg-orange-600 text-white">{data.score}%</Badge>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{data.insight}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform Recommendations - Mobile Optimized */}
      {result.platform_recommendations && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader className="px-3 sm:px-6 py-3 sm:py-6">
            <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600" />
              <span className="text-xs sm:text-base">Platform-Specific Optimization</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <div className="grid gap-2 sm:gap-4">
              {Object.entries(result.platform_recommendations).map(([platform, data]) => (
                <div key={platform} className={`p-2 sm:p-4 rounded-lg border-l-4 ${
                  platform === 'Instagram' ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' :
                  platform === 'LinkedIn' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' :
                  platform === 'TikTok' ? 'border-black bg-gray-50 dark:bg-gray-700' :
                  platform === 'Facebook' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' :
                  'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                }`}>
                  <div className="flex justify-between items-center mb-2 sm:mb-3">
                    <h5 className="font-bold text-sm sm:text-lg">{platform}</h5>
                    <Badge className="bg-gradient-to-r from-pink-600 to-purple-600 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                      Score: {data.score}%
                    </Badge>
                  </div>
                  
                  {/* Reasoning - Mobile Optimized */}
                  {data.reasoning && (
                    <p className={`text-xs sm:text-sm mb-2 sm:mb-3 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="font-semibold">Why: </span>{data.reasoning}
                    </p>
                  )}
                  
                  {/* Best Time to Post - Mobile Optimized */}
                  {data.best_time && (
                    <div className={`mb-2 sm:mb-3 p-1.5 sm:p-2 rounded ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'}`}>
                      <p className={`text-xs sm:text-sm leading-snug ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                        <span className="font-semibold">⏰ Best Time: </span>{data.best_time}
                      </p>
                    </div>
                  )}
                  
                  {/* Best Practices - Mobile Optimized */}
                  {data.best_practices && data.best_practices.length > 0 && (
                    <div className="mb-2 sm:mb-3">
                      <p className={`text-xs sm:text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Best Practices:</p>
                      <ul className={`text-[11px] sm:text-sm list-disc list-inside space-y-0.5 sm:space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {data.best_practices.map((practice, idx) => (
                          <li key={idx} className="leading-snug">{practice}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Content Tips - Mobile Optimized */}
                  {data.content_tips && (
                    <p className={`text-xs sm:text-sm mt-1.5 sm:mt-2 p-1.5 sm:p-2 rounded leading-snug ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'} border-l-2 border-purple-500`}>
                      💡 <span className="font-semibold">Tip:</span> {data.content_tips}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Behavioral Metrics - Mobile Optimized */}
      {result.behavioral_metrics && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader className="px-3 sm:px-6 py-3 sm:py-6">
            <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              <span className="text-xs sm:text-base">Behavioral Predictions</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
              {Object.entries(result.behavioral_metrics).map(([metric, score]) => (
                <div key={metric} className={`p-2 sm:p-4 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-green-50 to-blue-50'}`}>
                  <div className={`text-[10px] sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-0.5 sm:mb-1 capitalize leading-tight`}>
                    {metric.replace(/_/g, ' ')}
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-green-600">{score}%</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Psychological Profile */}
      {result.psychological_profile && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Brain className="w-5 h-5 text-indigo-600" />
              Psychological Profile (Big 5)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(result.psychological_profile).map(([trait, score]) => (
                <div key={trait}>
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trait.replace(/_/g, ' ')}</span>
                    <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{score}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full" style={{width: `${score}%`}}></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Impact */}
      {result.business_impact && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <DollarSign className="w-5 h-5 text-yellow-600" />
              Business Impact Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <div className="text-sm text-yellow-600 mb-1">Revenue Potential</div>
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {result.business_impact.revenue_potential}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <div className="text-sm text-blue-600 mb-1">Brand Alignment</div>
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {result.business_impact.brand_alignment}%
                </div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <div className="text-sm text-red-600 mb-1">Risk Level</div>
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} capitalize`}>
                  {result.business_impact.risk_level}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <div className="text-sm text-purple-600 mb-1">Viral Coefficient</div>
                <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {result.business_impact.viral_coefficient}%
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Psychographics */}
      {result.psychographics && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Heart className="w-5 h-5 text-rose-600" />
              Psychographic Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {result.psychographics.values && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-rose-50'}`}>
                  <h5 className="text-sm font-semibold text-rose-600 mb-2">Core Values</h5>
                  <div className="flex flex-wrap gap-2">
                    {result.psychographics.values.map((val, idx) => (
                      <Badge key={idx} className="bg-rose-600 text-white">{val}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.psychographics.interests && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-pink-50'}`}>
                  <h5 className="text-sm font-semibold text-pink-600 mb-2">Interests</h5>
                  <div className="flex flex-wrap gap-2">
                    {result.psychographics.interests.map((int, idx) => (
                      <Badge key={idx} className="bg-pink-600 text-white">{int}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.psychographics.lifestyle && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h5 className="text-sm font-semibold text-purple-600 mb-2">Lifestyle Segment</h5>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.psychographics.lifestyle}</p>
                </div>
              )}
              {result.psychographics.motivations && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-indigo-50'}`}>
                  <h5 className="text-sm font-semibold text-indigo-600 mb-2">Key Motivations</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.psychographics.motivations.map((mot, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{mot}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.psychographics.pain_points && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                  <h5 className="text-sm font-semibold text-orange-600 mb-2">Pain Points</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.psychographics.pain_points.map((pain, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{pain}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.psychographics.aspirations && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                  <h5 className="text-sm font-semibold text-cyan-600 mb-2">Aspirations</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.psychographics.aspirations.map((asp, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{asp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cultural Intelligence */}
      {result.cultural_intelligence && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <MapPin className="w-5 h-5 text-emerald-600" />
              Cultural Intelligence Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {Object.entries(result.cultural_intelligence).filter(([key]) => !key.includes('warnings') && !key.includes('tips')).map(([region, data]) => (
                (data?.sensitivity_score !== undefined || data?.sensitivity !== undefined) && (
                  <div key={region} className={`p-4 rounded-lg border-l-4 border-emerald-500 ${darkMode ? 'bg-gray-700' : 'bg-emerald-50'}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-semibold text-lg text-emerald-600">{region}</h5>
                      <Badge className="bg-emerald-600 text-white">
                        Sensitivity: {data.sensitivity_score || data.sensitivity}%
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Cultural Fit:</strong> {data.cultural_fit}
                      </p>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Language Tone:</strong> {data.language_tone}
                      </p>
                      {data.cultural_notes && (
                        <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          <strong>Cultural Notes:</strong> {data.cultural_notes}
                        </p>
                      )}
                      {data.warnings && (
                        <p className={`text-sm p-2 rounded ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          <strong>⚠️ Warning:</strong> {data.warnings}
                        </p>
                      )}
                      {data.localization_tips && (
                        <p className={`text-sm p-2 rounded ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          <strong>💡 Localization Tip:</strong> {data.localization_tips}
                        </p>
                      )}
                      {data.local_references && (
                        <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          <strong>Local Insights:</strong> {data.local_references}
                        </p>
                      )}
                    </div>
                  </div>
                )
              ))}
              {result.cultural_intelligence.cultural_warnings?.length > 0 && (
                <div className={`p-4 rounded-lg border-2 border-red-500 ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                  <h5 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Cultural Warnings
                  </h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.cultural_intelligence.cultural_warnings.map((warning, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Competitive Intelligence */}
      {result.competitive_intelligence && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Award className="w-5 h-5 text-amber-600" />
              Competitive Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className={`p-6 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-amber-50 to-yellow-50'}`}>
                <div className="text-sm text-amber-600 mb-1">Differentiation Score</div>
                <div className="text-4xl font-bold text-amber-600">{result.competitive_intelligence.differentiation_score}%</div>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-amber-50'}`}>
                <h5 className="text-sm font-semibold text-amber-600 mb-2">Market Positioning</h5>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.competitive_intelligence.market_positioning}</p>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <h5 className="text-sm font-semibold text-yellow-600 mb-2">Unique Angle</h5>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.competitive_intelligence.unique_angle}</p>
              </div>
              {result.competitive_intelligence.competitive_advantages?.length > 0 && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <h5 className="text-sm font-semibold text-green-600 mb-2">Competitive Advantages</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.competitive_intelligence.competitive_advantages.map((adv, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{adv}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* A/B Testing Recommendations */}
      {result.ab_testing_recommendations && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <TestTube className="w-5 h-5 text-cyan-600" />
              A/B Testing Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {Array.isArray(result.ab_testing_recommendations) ? (
                // Handle array structure
                result.ab_testing_recommendations.map((data, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border-l-4 border-cyan-500 ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-semibold text-lg text-cyan-600 capitalize">
                        {data.element}
                      </h5>
                      <Badge className={`${
                        data.priority === 'high' ? 'bg-red-600' :
                        data.priority === 'medium' ? 'bg-yellow-600' : 'bg-green-600'
                      } text-white`}>{data.priority}</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Element:</strong> {data.element}
                      </p>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Variation A:</strong> {data.variation_a}
                      </p>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Variation B:</strong> {data.variation_b}
                      </p>
                      <p className={`p-2 rounded ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        <strong>Hypothesis:</strong> {data.hypothesis}
                      </p>
                      {data.expected_impact && (
                        <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          <strong>Expected Impact:</strong> {data.expected_impact}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {data.duration && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <strong>Duration:</strong> {data.duration}
                          </p>
                        )}
                        {data.sample_size && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <strong>Sample:</strong> {data.sample_size}
                          </p>
                        )}
                      </div>
                      {data.success_metrics && data.success_metrics.length > 0 && (
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <strong>Success Metrics:</strong> {data.success_metrics.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                // Handle object structure (test_1, test_2, etc.)
                Object.entries(result.ab_testing_recommendations).filter(([key]) => key.startsWith('test_')).map(([key, data]) => (
                  <div key={key} className={`p-4 rounded-lg border-l-4 border-cyan-500 ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-semibold text-lg text-cyan-600 capitalize">
                        {data.element || key.replace('_', ' ')}
                      </h5>
                      <Badge className={`${
                        data.priority === 'high' ? 'bg-red-600' :
                        data.priority === 'medium' ? 'bg-yellow-600' : 'bg-green-600'
                      } text-white`}>{data.priority}</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Element:</strong> {data.element}
                      </p>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Variation A:</strong> {data.variation_a}
                      </p>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <strong>Variation B:</strong> {data.variation_b}
                      </p>
                      <p className={`p-2 rounded ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        <strong>Hypothesis:</strong> {data.hypothesis}
                      </p>
                      {data.expected_impact && (
                        <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          <strong>Expected Impact:</strong> {data.expected_impact}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {(data.test_duration || data.duration) && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <strong>Duration:</strong> {data.test_duration || data.duration}
                          </p>
                        )}
                        {data.sample_size && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <strong>Sample:</strong> {data.sample_size}
                          </p>
                        )}
                      </div>
                      {data.success_metrics && data.success_metrics.length > 0 && (
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <strong>Success Metrics:</strong> {data.success_metrics.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ROI Predictions */}
      {result.roi_predictions && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <DollarSign className="w-5 h-5 text-green-600" />
              ROI & Performance Predictions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(result.roi_predictions).map(([metric, value]) => (
                <div key={metric} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-green-50 to-emerald-50'}`}>
                  <div className="text-sm text-green-600 mb-1 capitalize">{metric.replace(/_/g, ' ')}</div>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {typeof value === 'number' ? `${value}%` : value}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Assessment */}
      {result.risk_assessment && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Shield className="w-5 h-5 text-red-600" />
              Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className={`p-6 rounded-lg text-center ${
                result.risk_assessment.overall_risk === 'low' ? 'bg-green-50 dark:bg-green-900/20' :
                result.risk_assessment.overall_risk === 'medium' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                'bg-red-50 dark:bg-red-900/20'
              }`}>
                <div className="text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} dark:text-gray-400 mb-1">Overall Risk</div>
                <div className={`text-4xl font-bold uppercase ${
                  result.risk_assessment.overall_risk === 'low' ? 'text-green-600' :
                  result.risk_assessment.overall_risk === 'medium' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>{result.risk_assessment.overall_risk}</div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {['brand_safety', 'controversy_potential', 'misinterpretation_risk'].map((metric) => (
                  result.risk_assessment[metric] !== undefined && (
                    <div key={metric} className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                      <div className="text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} dark:text-gray-400 mb-1 capitalize">{metric.replace(/_/g, ' ')}</div>
                      <div className="text-2xl font-bold text-red-600">{result.risk_assessment[metric]}%</div>
                    </div>
                  )
                ))}
              </div>
              {result.risk_assessment.mitigation_strategies?.length > 0 && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <h5 className="text-sm font-semibold text-blue-600 mb-2">Mitigation Strategies</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.risk_assessment.mitigation_strategies.map((strategy, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{strategy}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timing Recommendations */}
      {result.timing_recommendations && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Clock className="w-5 h-5 text-blue-600" />
              Timing & Seasonal Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {result.timing_recommendations.best_days?.length > 0 && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <h5 className="text-sm font-semibold text-blue-600 mb-2">Best Days</h5>
                  <div className="flex flex-wrap gap-2">
                    {result.timing_recommendations.best_days.map((day, idx) => (
                      <Badge key={idx} className="bg-blue-600 text-white">{day}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.timing_recommendations.best_times?.length > 0 && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-indigo-50'}`}>
                  <h5 className="text-sm font-semibold text-indigo-600 mb-2">Best Times</h5>
                  <div className="flex flex-wrap gap-2">
                    {result.timing_recommendations.best_times.map((time, idx) => (
                      <Badge key={idx} className="bg-indigo-600 text-white">{time}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.timing_recommendations.seasonal_fit && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h5 className="text-sm font-semibold text-purple-600 mb-2">Seasonal Fit</h5>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.timing_recommendations.seasonal_fit}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accessibility */}
      {result.accessibility && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Eye className="w-5 h-5 text-teal-600" />
              Accessibility & Inclusivity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {Object.entries(result.accessibility).map(([metric, value]) => (
                !Array.isArray(value) && (
                  <div key={metric} className={`flex justify-between items-center p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-teal-50'}`}>
                    <span className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {metric.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {typeof value === 'number' ? `${value}%` : value}
                    </span>
                  </div>
                )
              ))}
              {result.accessibility.accessibility_improvements?.length > 0 && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-teal-50'}`}>
                  <h5 className="text-sm font-semibold text-teal-600 mb-2">Suggested Improvements</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.accessibility.accessibility_improvements.map((improvement, idx) => (
                      <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{improvement}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strategic Recommendations - Mobile Optimized */}
      <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
        <CardHeader className="px-3 sm:px-6 py-3 sm:py-6">
          <CardTitle className={`flex items-center gap-2 text-sm sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            <span className="text-xs sm:text-base">Strategic Recommendations</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          {/* Custom Question Display - Show what user asked */}
          {result.custom_question && (
            <div className="mb-3 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500">
              <div className="flex items-start gap-2">
                <span className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Your Question:</span>
                <p className={`text-xs sm:text-sm italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{result.custom_question}"
                </p>
              </div>
            </div>
          )}
          
          {/* Custom Answer First - Mobile Optimized */}
          {result.custom_answer && (
            <div className="mb-3 sm:mb-6 p-3 sm:p-6 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Lightbulb className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
                <h3 className={`text-sm sm:text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  EmoQ Analysis
                </h3>
              </div>
              <p className={`text-xs sm:text-base leading-snug sm:leading-relaxed whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {result.custom_answer}
              </p>
            </div>
          )}
          
          {/* Recommendations List - Mobile Optimized */}
          <div className="space-y-2 sm:space-y-3">
            {result.recommendations?.map((rec, idx) => (
              <div key={idx} className={`flex gap-2 sm:gap-3 p-2 sm:p-4 rounded-lg ${darkMode ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'} border`}>
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-[10px] sm:text-xs font-bold flex-shrink-0">{idx + 1}</div>
                <p className={`text-xs sm:text-sm leading-snug sm:leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rec}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actionable Recommendations */}
      {result.actionable_recommendations && Array.isArray(result.actionable_recommendations) && result.actionable_recommendations.length > 0 && (
        <Card className={darkMode ? 'bg-gray-800/90 border-2 border-blue-500 shadow-2xl shadow-blue-500/20' : 'bg-white shadow-lg'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <CheckCircle className="w-5 h-5 text-green-600" />
              Actionable Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {result.actionable_recommendations.map((rec, idx) => (
                <div key={idx} className={`flex gap-3 p-3 rounded-lg ${darkMode ? 'bg-green-900/20 border-green-700/50' : 'bg-green-50 border-green-200'} border`}>
                  <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{String(rec)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComprehensiveAnalysisResults;
